import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  Phone,
  BellRing,
  Camera,
  DoorOpen,
  Radio,
  Wrench,
  Handshake,
  MapPin,
  Building2,
  Store,
  Factory,
  Warehouse,
  HeartPulse,
  GraduationCap,
  Hotel,
  KeyRound,
  Users,
  Search,
  AlertTriangle,
  CalendarClock,
  ClipboardCheck,
  Shield,
  Eye,
  Fingerprint,
  Cctv,
} from "lucide-react";
import TrustStrip from "../components/site/TrustStrip";
import CTABanner from "../components/site/CTABanner";
import ProcessSteps from "../components/site/ProcessSteps";
import FAQBlock from "../components/site/FAQBlock";
import Reveal from "../components/site/Reveal";
import Marquee from "../components/site/Marquee";
import useSEO from "../hooks/useSEO";

const HERO_IMG = "https://customer-assets-m6fa6gv7.emergentagent.net/job_fire-cctv-access/artifacts/4694gjo3_image.png";
const ABOUT_IMG = "https://customer-assets-m6fa6gv7.emergentagent.net/job_fire-cctv-access/artifacts/1go309ee_ChatGPT%20Image%20Aug%207%2C%202026%2C%2001_23_51%20PM.png";

const EASE = [0.22, 1, 0.36, 1];

export default function Home() {
  useSEO({
    title: "FSS | Fire Safety, CCTV, Access Control & Monitoring Ireland",
    description:
      "FSS provides complete fire safety, CCTV, security, access control and monitoring solutions for businesses and organisations across Ireland. Request a free site assessment today.",
    canonicalPath: "/",
  });

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroImgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);

  return (
    <>
      {/* Hero */}
      <section ref={heroRef} className="relative bg-white border-b border-slate-200 overflow-hidden">
        <div className="fss-grid-bg absolute inset-0 opacity-[0.5]" aria-hidden />
        <div className="fss-container relative py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: EASE }}
              className="fss-overline"
              data-testid="hero-eyebrow"
            >
              Facilities and Safety Systems · Ireland
            </motion.div>
            <h1 className="mt-5 fss-headline text-5xl sm:text-6xl lg:text-7xl text-slate-900">
              <span className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
                >
                  Protecting People,
                </motion.span>
              </span>
              <span className="block overflow-hidden pb-1">
                <motion.span
                  className="block text-green-700"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.32, ease: EASE }}
                >
                  Property and Premises
                </motion.span>
              </span>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.5, ease: EASE }}
              className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl"
            >
              Complete fire, security, access control and monitoring solutions from one trusted provider.
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.6, ease: EASE }}
              className="mt-4 text-base text-slate-500 leading-relaxed max-w-xl"
            >
              FSS supports customers with professional system design, installation, commissioning, testing, servicing, maintenance, repairs, upgrades and ongoing support across Ireland.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.72, ease: EASE }}
              className="mt-8 flex flex-wrap gap-3"
            >
              <Link to="/contact" className="fss-btn fss-btn-primary" data-testid="hero-cta-quote">Request a Free Site Assessment <ArrowRight className="w-4 h-4 ml-2" /></Link>
              <Link to="/services" className="fss-btn fss-btn-outline" data-testid="hero-cta-services">Explore Our Services</Link>
              <a href="tel:018351454" className="fss-btn fss-btn-outline" data-testid="hero-cta-call"><Phone className="w-4 h-4 mr-2" />Contact FSS</a>
            </motion.div>
          </div>

          <motion.div
            className="lg:col-span-5 relative"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.35, ease: EASE }}
          >
            <div className="relative aspect-[4/5] overflow-hidden border border-slate-200 rounded-sm shadow-sm">
              <motion.img
                src={HERO_IMG}
                alt="Integrated fire, CCTV, security and access control systems supported by FSS"
                className="w-full h-full object-cover"
                style={{ y: heroImgY, scale: 1.18, objectPosition: "20% 40%", transformOrigin: "20% 40%" }}
              />
              <div className="absolute top-0 left-0 w-2 h-32 bg-red-600" />
              <div className="absolute bottom-0 right-0 w-32 h-2 bg-green-700" />
            </div>
          </motion.div>
        </div>
      </section>

      <Marquee />

      <TrustStrip />

      {/* 4 Divisions */}
      <section className="bg-white fss-section" id="divisions" data-testid="four-divisions">
        <div className="fss-container">
          <Reveal className="max-w-3xl mb-12">
            <div className="fss-overline">Four core divisions</div>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-semibold text-slate-900 leading-tight">One provider. Every system. Fully integrated.</h2>
            <p className="mt-3 text-slate-600">
              FSS provides complete fire safety, CCTV, security, access control and monitoring solutions — designed, installed, tested, maintained and supported by one team.
            </p>
          </Reveal>
          <Reveal delay={0.12}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
            {DIVISIONS.map((d) => (
              <div key={d.slug} className="bg-white p-8 group hover:bg-slate-900 transition-colors duration-300 relative" data-testid={`division-${d.slug}`}>
                <div className="absolute top-0 left-0 right-0 h-1 bg-green-700 group-hover:bg-red-600 transition-colors" />
                <d.icon className="w-10 h-10 text-green-700 group-hover:text-green-500 transition-colors" strokeWidth={1.75} />
                <h3 className="mt-5 font-display text-xl font-semibold text-slate-900 group-hover:text-white transition-colors">{d.title}</h3>
                <p className="mt-3 text-sm text-slate-600 group-hover:text-slate-300 leading-relaxed transition-colors">{d.copy}</p>
                <Link to={d.to} className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-green-700 group-hover:text-green-500 transition-colors">
                  {d.cta}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            ))}
          </div>
          </Reveal>
        </div>
      </section>

      {/* About */}
      <section className="fss-section bg-slate-50 border-y border-slate-200">
        <div className="fss-container grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <Reveal className="lg:col-span-5">
            <div className="fss-overline">About FSS</div>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-semibold text-slate-900 leading-tight">Complete fire, security & access solutions</h2>
            <div className="mt-6 aspect-[4/3] overflow-hidden border border-slate-200 rounded-sm hidden lg:block">
              <img src={ABOUT_IMG} alt="FSS security officer checking CCTV and alarm systems at night" className="w-full h-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-7 space-y-5 text-slate-600 leading-relaxed">
            <p>
              FSS provides complete fire safety, CCTV, security, access control and monitoring solutions for businesses and organisations across Ireland.
            </p>
            <p>
              From fire alarm systems and emergency lighting to CCTV surveillance, intruder alarms, controlled entry and professional monitoring, FSS designs, installs, tests, maintains and supports integrated systems tailored to each premises.
            </p>
            <p>
              From a single fire extinguisher service or CCTV installation to an integrated multi-site fire, security and access control solution, FSS provides practical advice, dependable service and ongoing technical support.
            </p>
            <div className="pt-2">
              <Link to="/about" className="fss-btn fss-btn-outline" data-testid="about-learn-more">About Fire Safety Systems</Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Why Choose */}
      <section className="fss-section bg-white">
        <div className="fss-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-5">
            <div className="fss-overline">Why choose FSS</div>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-semibold text-slate-900 leading-tight">Four divisions. One trusted provider.</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              FSS supports customers through the full lifecycle of their systems — design, installation, commissioning, testing, servicing, repairs and upgrades — with clear advice and dependable delivery.
            </p>
            <Link to="/contact" className="mt-8 fss-btn fss-btn-primary inline-flex" data-testid="why-cta">Request a Free Site Assessment</Link>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-7">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
            {[
              { icon: Handshake, title: "Complete fire, security & monitoring support" },
              { icon: Users, title: "Professional advice" },
              { icon: Building2, title: "Tailored to your premises" },
              { icon: BellRing, title: "Reliable installations" },
              { icon: ClipboardCheck, title: "Scheduled testing and servicing" },
              { icon: CalendarClock, title: "Planned maintenance programmes" },
              { icon: Search, title: "Clear service documentation" },
              { icon: Wrench, title: "Support for new and existing systems" },
              { icon: MapPin, title: "Single-site and multi-site coverage" },
              { icon: Phone, title: "Responsive customer support" },
            ].map((it, i) => (
              <div key={i} className="bg-white p-5 flex items-start gap-3" data-testid={`why-item-${i}`}>
                <it.icon className="w-5 h-5 text-green-700 shrink-0 mt-0.5" strokeWidth={1.75} />
                <h3 className="font-display text-sm font-semibold text-slate-900 leading-tight">{it.title}</h3>
              </div>
            ))}
          </div>
          </Reveal>
        </div>
      </section>

      {/* Industries */}
      <section className="bg-slate-50 fss-section border-y border-slate-200">
        <div className="fss-container">
          <Reveal className="max-w-2xl mb-10">
            <div className="fss-overline">Industries we support</div>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-semibold text-slate-900 leading-tight">Every premises is different.</h2>
            <p className="mt-3 text-slate-600">
              Different fire risks, security needs, occupancy requirements and maintenance considerations. We provide tailored fire, security, access and monitoring solutions to suit the building.
            </p>
          </Reveal>
          <Reveal delay={0.12}>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-px bg-slate-200 border border-slate-200">
            {[
              { icon: Building2, title: "Commercial Offices" }, { icon: Store, title: "Retail" }, { icon: Factory, title: "Manufacturing" },
              { icon: Warehouse, title: "Warehousing" }, { icon: HeartPulse, title: "Healthcare" }, { icon: GraduationCap, title: "Education" },
              { icon: Hotel, title: "Hospitality" }, { icon: KeyRound, title: "Property Management" }, { icon: Factory, title: "Industrial" },
              { icon: Building2, title: "Multi-site Organisations" },
            ].map((it, i) => (
              <div key={i} className="bg-white p-5 flex items-center gap-3" data-testid={`industry-${i}`}>
                <it.icon className="w-5 h-5 text-green-700 shrink-0" strokeWidth={1.75} />
                <div className="font-display font-semibold text-sm text-slate-900">{it.title}</div>
              </div>
            ))}
          </div>
          </Reveal>
          <div className="mt-8">
            <Link to="/industries" className="fss-btn fss-btn-outline">View Industries<ArrowRight className="w-4 h-4 ml-2" /></Link>
          </div>
        </div>
      </section>

      {/* Process */}
      <ProcessSteps
        eyebrow="How we work"
        title="Our installation and service process"
        intro="Not every enquiry requires all six stages — we adapt the approach depending on the service and your premises."
        steps={[
          { title: "Initial Consultation", copy: "We discuss the premises, current systems and the service you require." },
          { title: "Site Survey", copy: "Where required, we assess the site, installed equipment and requirements." },
          { title: "Recommendation & Quotation", copy: "You receive a clear proposed scope of work and quotation." },
          { title: "Installation or Service", copy: "The agreed installation, inspection, testing, repair or maintenance is completed." },
          { title: "Documentation & Handover", copy: "Relevant service information, records and documentation are provided." },
          { title: "Ongoing Support", copy: "We provide future servicing, planned maintenance, repairs and system upgrades." },
        ]}
      />

      <FAQBlock
        eyebrow="Homepage FAQ"
        title="Common questions across all four divisions"
        items={[
          { q: "What does FSS provide?", a: "Fire safety, CCTV & security, access control and monitoring systems — designed, installed, tested and maintained across Ireland." },
          { q: "Does FSS work with existing systems?", a: "Yes — we support existing systems that need maintenance, repair, extension or upgrading, as well as brand-new installations." },
          { q: "Can FSS support multiple sites?", a: "Yes — we coordinate multi-site programmes with site-by-site reporting and central records." },
          { q: "How do I arrange a site assessment?", a: "Use the enquiry form or call 01 835 1454. We discuss your premises, current systems and requirements." },
          { q: "Does FSS integrate systems across divisions?", a: "Yes — CCTV, intruder alarms, access control and monitoring can be integrated so they work together as a single security ecosystem." },
        ]}
      />

      <CTABanner
        title="Protect people, property and premises today"
        subtitle="Contact FSS to discuss fire safety, CCTV, security, access control or monitoring for your premises."
        primary={{ to: "/contact", label: "Request a Free Site Assessment" }}
        secondary={{ to: "/services", label: "Explore Our Services" }}
      />
    </>
  );
}

const DIVISIONS = [
  {
    slug: "fire",
    icon: Shield,
    title: "Fire Safety Systems",
    copy: "Complete fire detection, protection, testing and maintenance solutions for commercial, industrial and public-sector premises.",
    to: "/services",
    cta: "Explore Fire Safety",
  },
  {
    slug: "cctv",
    icon: Cctv,
    title: "CCTV & Security Systems",
    copy: "Intelligent CCTV, intruder alarm and perimeter protection systems designed to detect threats and protect people, property and premises.",
    to: "/cctv-security",
    cta: "Explore CCTV & Security",
  },
  {
    slug: "access",
    icon: Fingerprint,
    title: "Access Control & Entry Systems",
    copy: "Secure and scalable access control, intercom and entry management systems for doors, gates, barriers and restricted areas.",
    to: "/access-control",
    cta: "Explore Access Control",
  },
  {
    slug: "monitoring",
    icon: Eye,
    title: "Monitoring & Personal Protection",
    copy: "Professional monitoring, verified alerts and personal protection solutions designed to help identify and escalate incidents quickly.",
    to: "/monitoring",
    cta: "Explore Monitoring",
  },
];
